import { useState, useEffect, useCallback, useRef, lazy, Suspense, useMemo } from 'react'
import { Header } from '@/components/Layout/Header'
import { ZoneSidebar } from '@/components/Layout/ZoneSidebar'
import { useTheme } from '@/hooks/use-theme'
import { useBranding } from '@/hooks/use-branding'
import { BrandIdentity } from '@/components/Layout/BrandIdentity'
import { PoweredByParkeze } from '@/components/Layout/PoweredByParkeze'
import parkezeMark from '@/assets/parkeze-mark.png'
import { 
  useSensorsAndZonesByLocation,
  useFacilitiesByLocation,
  useOperators,
} from '@/services/api'

// Lazy load heavy components
const ParkingMapGL = lazy(() => import('@/components/Map/ParkingMapGL').then(module => ({ default: module.ParkingMapGL })))
const Turnstile = lazy(() => import('@/components/ui/turnstile').then(module => ({ default: module.Turnstile })))
const EventLayer = lazy(() => import('@/components/Events/EventLayer').then(module => ({ default: module.EventLayer })))
import { EventCard, EventList, EventsControl } from '@/components/Events/EventPanel'
import { useEvent, useEventsInView } from '@/services/events'
import type { EventSummary, Zone } from '@/types'
import FloorStack from '@parkeze/map-kit/map/floors/FloorStack'
import { garageAt, topFloor, type Garage } from '@parkeze/map-kit/map/floors/floors'
import { toGarage } from '@/lib/garages'
import type { Map as MapboxMap } from 'mapbox-gl'

// Map style type
type MapStyleType = 'map' | 'satellite'

// Default location (USC campus)
const DEFAULT_LOCATION = {
  latitude: 33.9977,
  longitude: -81.0282,
}

// Calculate viewport radius from map bounds
function calculateViewportRadius(bounds: { northEast: { lat: number; lng: number }; southWest: { lat: number; lng: number } }): number {
  const { northEast, southWest } = bounds
  
  // Calculate the diagonal distance of the viewport
  const latDiff = northEast.lat - southWest.lat
  const lngDiff = northEast.lng - southWest.lng
  
  // Use the larger dimension and convert to meters (rough approximation)
  // 1 degree latitude ≈ 111,000 meters
  // 1 degree longitude ≈ 111,000 * cos(lat) meters
  const latRadius = (latDiff * 111000) / 2
  const lngRadius = (lngDiff * 111000 * Math.cos((northEast.lat + southWest.lat) / 2 * Math.PI / 180)) / 2
  
  // Use the larger radius, but cap it at 5km for cost control
  const radius = Math.max(latRadius, lngRadius)
  return Math.min(radius, 5000) // Max 5km radius
}

function App() {
  // Initialize system theme on app load
  useTheme()
  
  // Branding for /u/:realm routes
  const { brand } = useBranding()
  
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [userLocation, setUserLocation] = useState(DEFAULT_LOCATION)
  const [hasLocationPermission, setHasLocationPermission] = useState(false)
  const [mapCenter, setMapCenter] = useState(DEFAULT_LOCATION)
  const [mapBounds, setMapBounds] = useState<{ northEast: { lat: number; lng: number }; southWest: { lat: number; lng: number } } | null>(null)
  const [debouncedMapCenter, setDebouncedMapCenter] = useState(DEFAULT_LOCATION)
  const [rateLimitError, setRateLimitError] = useState(false)
  const [isVerified, setIsVerified] = useState(false)
  // Persist map type in localStorage
  const [mapType, setMapType] = useState<MapStyleType>(() => {
    const stored = localStorage.getItem('parkeze-map-type')
    return (stored === 'map' || stored === 'satellite') ? stored : 'map'
  })
  
  const mapRef = useRef<MapboxMap | null>(null)
  
  // Save map type to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('parkeze-map-type', mapType)
  }, [mapType])
  
  // Debounce map center changes to prevent excessive API calls
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedMapCenter(mapCenter)
    }, 150) // 150ms debounce - reduced for more responsive updates
    
    return () => clearTimeout(timeoutId)
  }, [mapCenter])
  
  // Fetch sensors and zones by location together (avoids duplicate API calls)
  const { data: locationData, isLoading: sensorsLoading, error: sensorsError } = useSensorsAndZonesByLocation(
    debouncedMapCenter.latitude,
    debouncedMapCenter.longitude,
    mapBounds ? calculateViewportRadius(mapBounds) : 2000, // Use viewport radius or fallback
    isVerified // Only fetch once the user has passed the captcha
  )
  
  // Garages in view and their floors (decision 49)
  const { data: allFacilities = [] } = useFacilitiesByLocation(
    debouncedMapCenter.latitude,
    debouncedMapCenter.longitude,
    mapBounds ? calculateViewportRadius(mapBounds) : 2000,
    isVerified
  )

  // Events at venues in view and their parking (decision 109): pins, a card that books, a list.
  const [showEvents, setShowEvents] = useState(() => localStorage.getItem('parkeze-events') !== 'off')
  useEffect(() => {
    localStorage.setItem('parkeze-events', showEvents ? 'on' : 'off')
  }, [showEvents])
  const { data: events = [] } = useEventsInView(
    mapBounds && { west: mapBounds.southWest.lng, south: mapBounds.southWest.lat, east: mapBounds.northEast.lng, north: mapBounds.northEast.lat },
    isVerified && showEvents,
  )
  const [eventId, setEventId] = useState<string | null>(null)
  // The list open: every event in view (null), or one venue's, opened from its pin.
  const [eventList, setEventList] = useState<EventSummary[] | null | false>(false)
  const { data: eventDetail, isError: eventFailed } = useEvent(eventId)
  // The box asked for is rounded outward; the count and the list are what is on screen.
  const eventsInView = useMemo(() => !mapBounds ? events : events.filter(e =>
    e.venue.lat >= mapBounds.southWest.lat && e.venue.lat <= mapBounds.northEast.lat &&
    e.venue.lng >= mapBounds.southWest.lng && e.venue.lng <= mapBounds.northEast.lng), [events, mapBounds])
  const listed = eventList === false ? null : eventList ?? eventsInView
  const closeEvents = useCallback(() => {
    setEventId(null)
    setEventList(false)
  }, [])

  // Extract sensors and zones from the combined response
  const allSensors = locationData?.sensors || []
  const allZones = locationData?.zones || []
  
  // Fetch operators for zone display
  const { data: operators = [] } = useOperators()
  
  // Find the operator matching the branded realm (if any)
  const brandedOperator = useMemo(() => {
    if (!brand) return null
    return operators.find(op => op.keycloak_realm === brand.alias) ?? null
  }, [brand, operators])
  
  // Filter sensors and zones to branded operator when on a /u/:realm route
  const sensors = useMemo(() => {
    if (!brandedOperator) return allSensors
    return allSensors.filter(s => s.operator_id === brandedOperator.id)
  }, [allSensors, brandedOperator])
  
  const zones = useMemo(() => {
    if (!brandedOperator) return allZones
    return allZones.filter(z => z.operator_id === brandedOperator.id)
  }, [allZones, brandedOperator])

  const facilities = useMemo(() => {
    if (!brandedOperator) return allFacilities
    return allFacilities.filter(f => f.operator_id === brandedOperator.id)
  }, [allFacilities, brandedOperator])

  // A garage's floors (decision 49): the one each shows (its top until another is chosen), and
  // the garage whose floor stack is pointed at, drawn with every floor.
  const garages = useMemo(() => facilities.filter(f => f.kind !== 'street').map(toGarage), [facilities])
  const [chosenFloor, setChosenFloor] = useState<Record<string, string>>({})
  const [floorPreview, setFloorPreview] = useState<{ garageId: string; floorId: string | null } | null>(null)
  const shownFloor = useCallback((g: Garage) => {
    const chosen = chosenFloor[g.id]
    return chosen && g.floors.some(f => f.id === chosen) ? chosen : topFloor(g)
  }, [chosenFloor])
  const showFloor = useCallback((garageId: string, floorId: string) => {
    setChosenFloor(c => ({ ...c, [garageId]: floorId }))
  }, [])
  // The garage the map is on, close enough to see its spaces: its floor stack shows.
  const focusedGarage = useMemo(() => {
    if ((mapRef.current?.getZoom() ?? 0) < 16) return null
    return garageAt(garages, [mapCenter.longitude, mapCenter.latitude])
  }, [garages, mapCenter])
  // A space on a floor not shown is not drawn: a garage's floors are at the same spot.
  const floorOf = useMemo(() => {
    const out = new Map<string, Garage>()
    for (const g of garages) if (g.floors.length > 1) for (const f of g.floors) out.set(f.id, g)
    return out
  }, [garages])
  const drawnSensors = useMemo(() => sensors.filter(s => {
    const g = s.level_id ? floorOf.get(s.level_id) : undefined
    return !g || shownFloor(g) === s.level_id
  }), [sensors, floorOf, shownFloor])
  
  // Handle rate limit errors
  useEffect(() => {
    if (sensorsError) {
      // Check if it's a rate limit error (429)
      if (sensorsError.message?.includes('429') || sensorsError.message?.includes('Rate limit')) {
        setRateLimitError(true)
      }
    } else {
      setRateLimitError(false)
    }
  }, [sensorsError])
  
  // SSE disabled - sensors fetched on page load only
  
  // Request user location on mount
  useEffect(() => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          })
          setHasLocationPermission(true)
        },
        () => {
          // Silently handle geolocation errors
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000,
        }
      )
    }
  }, [])
  
  // Handle Turnstile verification
  const handleTurnstileSuccess = useCallback((_token: string) => {
    setIsVerified(true)
  }, [])
  
  // Handle sensor click
  const handleSensorClick = useCallback(() => {
    // Sensor clicked - popup is handled by ParkingMapGL
  }, [])
  
  // Handle locate me button
  const handleLocateMe = useCallback(() => {
    if (!mapRef.current) return
    
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords
          setUserLocation({ latitude, longitude })
          setHasLocationPermission(true)
          mapRef.current?.flyTo({ center: [longitude, latitude], zoom: 16 })
        },
        () => {
          // Silently handle geolocation errors
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
        }
      )
    } else {
      alert('Geolocation is not supported by your browser.')
    }
  }, [])
  
  // Handle map move for data updates (now handled via onMapMove prop)
  const handleMapMove = useCallback((bounds: { north: number; south: number; east: number; west: number }, center: { lat: number; lng: number }) => {
    setMapCenter({
      latitude: center.lat,
      longitude: center.lng,
    })
    setMapBounds({
      northEast: { lat: bounds.north, lng: bounds.east },
      southWest: { lat: bounds.south, lng: bounds.west }
    })
  }, [])
  
  // Handle map click - fly to location and search for parking there
  const handleMapClick = useCallback((lat: number, lng: number) => {
    // Update map center to trigger data fetch for this location
    setMapCenter({
      latitude: lat,
      longitude: lng,
    })
    
    // Fly to the clicked location
    if (mapRef.current) {
      mapRef.current.flyTo({
        center: [lng, lat],
        zoom: Math.max(mapRef.current.getZoom(), 15), // At least zoom 15
      })
    }
  }, [])
  
  // Handle zone click - auto-zoom to show all zone sensors
  const handleZoneClick = useCallback((zone: Zone) => {
    if (!mapRef.current) return
    
    // Get all sensors in this zone
    const zoneSensors = sensors.filter(sensor => sensor.zone_id === zone.zone_id)
    
    if (zoneSensors.length === 0) {
      // Fallback to zone center if no sensors
      mapRef.current.flyTo({
        center: [zone.center_coordinates.lon, zone.center_coordinates.lat],
        zoom: 16,
      })
      return
    }
    
    if (zoneSensors.length === 1) {
      // Single sensor - zoom to a reasonable level around it
      mapRef.current.flyTo({
        center: [zoneSensors[0].lon, zoneSensors[0].lat],
        zoom: 18,
      })
      return
    }
    
    // Calculate bounds from all sensors in the zone
    let minLat = Infinity
    let maxLat = -Infinity
    let minLng = Infinity
    let maxLng = -Infinity
    
    zoneSensors.forEach(sensor => {
      minLat = Math.min(minLat, sensor.lat)
      maxLat = Math.max(maxLat, sensor.lat)
      minLng = Math.min(minLng, sensor.lon)
      maxLng = Math.max(maxLng, sensor.lon)
    })
    
    // Add some padding to the bounds (10% on each side)
    const latPadding = (maxLat - minLat) * 0.1
    const lngPadding = (maxLng - minLng) * 0.1
    
    // Fit the map to the bounds with animation
    mapRef.current.fitBounds(
      [[minLng - lngPadding, minLat - latPadding], [maxLng + lngPadding, maxLat + latPadding]],
      { padding: 40, maxZoom: 20 }
    )
  }, [sensors])
  
  // Handle address search - zoom to location and find nearest parking
  const handleLocationSearch = useCallback((lat: number, lng: number, placeName: string) => {
    if (!mapRef.current) return
    
    // Zoom to the searched location
    mapRef.current.flyTo({ center: [lng, lat], zoom: 17 })
    
    console.log('Searching for parking near:', placeName)
  }, [])
  
  // Calculate sensor counts
  const availableCount = sensors.filter(s => s.occupancy_status === false).length
  const occupiedCount = sensors.filter(s => s.occupancy_status === true).length
  
  const isLoading = sensorsLoading
  
  // Show Turnstile verification if not verified
  if (!isVerified) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-background px-4">
        <div className="max-w-md w-full mx-auto p-6 text-center space-y-6">
          <div className="space-y-3">
            {brand ? (
              <div className="flex justify-center">
                <BrandIdentity brand={brand} height={48} maxWidth={220} nameClassName="text-lg" centered />
              </div>
            ) : (
              <img 
                src={parkezeMark} 
                alt="Parkeze" 
                className="h-10 sm:h-12 w-auto mx-auto"
              />
            )}
            <h1 className="text-xl sm:text-2xl font-bold">
              {brand ? `${brand.name} Parking` : 'Parkeze Live Map'}
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground">
              Please verify you're human to access the parking map.
            </p>
          </div>
          
          <Suspense fallback={<div className="flex items-center justify-center p-4"><div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" /></div>}>
            <Turnstile
              // In development, Cloudflare's testing key, which always passes (as the README's setup has it)
              siteKey={import.meta.env.DEV ? '1x00000000000000000000AA' : '0x4AAAAAACJ_6qfu-3yvdasK'}
              theme="auto"
              onSuccess={handleTurnstileSuccess}
              onError={() => {}}
              onExpired={() => {}}
              className="mx-auto"
            />
          </Suspense>
          
          <p className="text-[11px] sm:text-xs text-muted-foreground">
            This helps prevent automated access to our parking data.
          </p>
        </div>
      </div>
    )
  }
  
  return (
    <div className="fixed inset-0 flex flex-col bg-background" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
      <Header 
        onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
        onLocateMe={handleLocateMe}
        onLocationSearch={handleLocationSearch}
        availableCount={availableCount}
        occupiedCount={occupiedCount}
        mapType={mapType}
        onMapTypeChange={setMapType}
        workerUrl="https://map-proxy.parkezesolutions.net"
        userLocation={hasLocationPermission ? userLocation : null}
        brand={brand}
      />
      
      {/* Main content - fills remaining space */}
      <main className="flex-1 relative mt-14 md:mt-14 mb-7">
        <Suspense fallback={<div className="flex items-center justify-center h-full"><div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" /></div>}>
          <ParkingMapGL
            sensors={drawnSensors}
            allSensors={sensors}
            zones={zones}
            facilities={facilities}
            garages={garages}
            shownFloor={shownFloor}
            floorPreview={floorPreview}
            onShowFloor={showFloor}
            onSensorClick={handleSensorClick}
            mapRef={mapRef}
            mapType={mapType}
            onMapMove={handleMapMove}
            onMapClick={handleMapClick}
            overlay={(map) => showEvents && (
              <Suspense fallback={null}>
                <EventLayer
                  map={map}
                  events={events}
                  selectedId={eventId}
                  selected={eventDetail}
                  onSelect={setEventId}
                  onOpenGroup={(group) => { setEventId(null); setEventList(group) }}
                  onClear={closeEvents}
                />
              </Suspense>
            )}
          />
        </Suspense>
        <EventsControl
          on={showEvents}
          count={eventsInView.length}
          onToggle={() => { setShowEvents(!showEvents); closeEvents() }}
          onList={() => { setEventId(null); setEventList(null) }}
        />
        {focusedGarage && focusedGarage.floors.length > 1 && (
          <FloorStack garage={focusedGarage} shown={shownFloor(focusedGarage)}
                      onShow={(id) => showFloor(focusedGarage.id, id)}
                      onPreview={(floorId) => setFloorPreview(floorId === undefined ? null : { garageId: focusedGarage.id, floorId })} />
        )}
      </main>
      
      {showEvents && eventId ? (
        <EventCard
          summary={[...events, ...(listed ?? [])].find(e => e.id === eventId)}
          detail={eventDetail?.id === eventId ? eventDetail : undefined}
          failed={eventFailed}
          onBack={listed ? () => setEventId(null) : undefined}
          onClose={closeEvents}
        />
      ) : showEvents && listed ? (
        <EventList events={listed} atOneVenue={eventList !== null} onSelect={setEventId} onClose={closeEvents} />
      ) : null}

      {/* Zone Sidebar */}
      <ZoneSidebar
        zones={zones}
        sensors={sensors}
        operators={operators}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onZoneClick={handleZoneClick}
        userLocation={hasLocationPermission ? userLocation : null}
      />
      
      {/* Rate Limit Error Popup */}
      {rateLimitError && (
        <div className="fixed inset-0 bg-black/50 z-[2000] flex items-center justify-center p-4">
          <div className="bg-background border border-border rounded-lg p-6 max-w-md mx-auto text-center space-y-4">
            <div className="space-y-2">
              <h3 className="text-lg font-semibold">Map is busy right now</h3>
              <p className="text-muted-foreground">
                Too many people are looking at the map at the same time. Please try again in a few minutes.
              </p>
            </div>
            <button
              onClick={() => setRateLimitError(false)}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
            >
              Okay
            </button>
          </div>
        </div>
      )}
      
      {/* Footer - Mobile Optimized */}
      <footer className="fixed bottom-0 left-0 right-0 z-40 bg-background/95 backdrop-blur-sm" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div className="px-3 sm:px-4 py-1.5 sm:py-1">
          <div className="flex items-center justify-between text-[10px] sm:text-xs text-muted-foreground">
            {/* Left: Copyright and links */}
            <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
              <span>© 2026 Parkeze</span>
              <span className="text-border">|</span>
              <a href="https://www.parkeze.com/privacy-policy" target="_blank" rel="noopener noreferrer" className="hover:underline">Privacy</a>
              <span className="text-border">|</span>
              <a href="https://www.parkeze.com/terms-of-service" target="_blank" rel="noopener noreferrer" className="hover:underline">Terms</a>
            </div>
            
            {/* Right: the Parkeze signature on a branded map, shown on phones
                too (decision 26); on Parkeze's own map, the sales line, which
                does not fit beside the links on a phone. */}
            {brand?.powered_by ? (
              <PoweredByParkeze />
            ) : !brand ? (
              <div className="hidden sm:flex items-center">
                <span>Want to see your parking spots here? </span>
                <a
                  href="mailto:hello@parkeze.com"
                  className="text-primary hover:underline font-medium ml-1"
                >
                  hello@parkeze.com
                </a>
              </div>
            ) : null}
          </div>
        </div>
      </footer>
      
      {/* Loading indicator */}
      {isLoading && (
        <div className="fixed bottom-12 left-3 sm:left-4 bg-background/90 backdrop-blur-sm border border-border rounded-lg px-3 py-2 text-sm flex items-center gap-2 z-50 shadow-lg" style={{ marginBottom: 'env(safe-area-inset-bottom)' }}>
          <div className="animate-spin w-4 h-4 border-2 border-primary border-t-transparent rounded-full" />
          <span className="hidden sm:inline">Loading sensors...</span>
          <span className="sm:hidden">Loading...</span>
        </div>
      )}
      
      {/* No location warning */}
      {!hasLocationPermission && (
        <div className="fixed bottom-12 left-3 sm:left-4 bg-background/90 backdrop-blur-sm border border-border rounded-lg px-3 py-2 text-xs sm:text-sm z-50 shadow-lg" style={{ marginBottom: 'env(safe-area-inset-bottom)' }}>
          <span className="text-muted-foreground">
            <span className="hidden sm:inline">Using default location. Enable location for nearby sensors.</span>
            <span className="sm:hidden">Enable location for nearby spots</span>
          </span>
        </div>
      )}
    </div>
  )
}

export default App
